import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import MainLayout from "@/components/layout/MainLayout";
import { SuperAdminLayout } from "@/components/super-admin/SuperAdminLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { Building2, Copy, ExternalLink, ArrowLeft } from "lucide-react";
import {
  superAdminOrgService,
  OrgHeader,
} from "@/services/superAdminOrgService";
import { organizationMonitoringService } from "@/services/organizationMonitoringService";
import OrganizationOverviewTab from "./tabs/OrganizationOverviewTab";
import OrganizationBillingTabEnhanced from "./tabs/OrganizationBillingTabEnhanced";
import { MembersTab } from "@/components/super-admin/MembersTab";
import {
  SkeletonAvatar,
  SkeletonText,
  SkeletonCard,
  Skeleton,
} from "@/components/ui/skeleton";

const OrganizationDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [org, setOrg] = useState<OrgHeader | null>(null);

  const activeTab = searchParams.get("tab") || "overview";

  useEffect(() => {
    const fetch = async () => {
      if (!id) return;
      setLoading(true);
      const { data, error } = await superAdminOrgService.getOrgHeader(id);
      if (error) {
        toast({
          variant: "destructive",
          title: "Failed to load organization",
          description: error.message,
        });
      }
      setOrg(data);
      setLoading(false);
    };
    fetch();
  }, [id, toast]);

  const subscriptionChip = useMemo(() => {
    const display = organizationMonitoringService.getSubscriptionStatusDisplay(
      org?.subscription_status || null,
    );
    return (
      <Badge variant={display.variant} className={display.className}>
        {display.text}
      </Badge>
    );
  }, [org?.subscription_status]);

  const actions = (
    <div className="flex items-center gap-2">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate("/super-admin/organizations")}
        className="mr-2"
      >
        <ArrowLeft className="h-4 w-4 mr-1" />
        Back
      </Button>
      <Button
        variant="outline"
        size="sm"
        disabled={!org?.stripe_customer_id}
        onClick={async () => {
          if (!org?.id) return;
          const { url, error } = await superAdminOrgService.openBillingPortal(
            org.id,
          );
          if (error || !url) {
            toast({
              variant: "destructive",
              title: "Billing portal",
              description:
                error?.message ||
                "Could not open billing portal. Ensure a Stripe customer exists.",
            });
            return;
          }
          window.location.href = url;
        }}
      >
        Open Billing Portal
      </Button>
      <Button
        variant="outline"
        size="sm"
        onClick={async () => {
          if (!org?.id) return;
          await navigator.clipboard.writeText(org.id);
          toast({ title: "Copied", description: "Organization ID copied" });
        }}
      >
        <Copy className="h-4 w-4 mr-1" /> Org ID
      </Button>
      <Button
        variant="outline"
        size="sm"
        disabled={!org?.stripe_customer_id}
        onClick={async () => {
          if (!org?.stripe_customer_id) return;
          await navigator.clipboard.writeText(org.stripe_customer_id);
          toast({
            title: "Copied",
            description: "Stripe Customer ID copied",
          });
        }}
      >
        <Copy className="h-4 w-4 mr-1" /> Customer ID
      </Button>
    </div>
  );

  const tabs = [
    { id: "overview", label: "Overview" },
    { id: "billing", label: "Billing" },
    { id: "members", label: "Members" },
    { id: "usage", label: "Usage & Limits" },
    { id: "agents", label: "Agents" },
    { id: "assistants", label: "Assistants" },
    { id: "knowledge", label: "Knowledge & Files" },
    { id: "integrations", label: "Integrations" },
    { id: "settings", label: "Settings & Flags" },
    { id: "audit", label: "Audit Logs" },
    { id: "events", label: "Webhooks/Events" },
  ];

  const handleTabChange = (tabId: string) => {
    const q = new URLSearchParams(searchParams);
    q.set("tab", tabId);
    setSearchParams(q, { replace: true });
  };

  return (
    <MainLayout>
      <SuperAdminLayout
        title={org?.name || "Organization"}
        actions={actions}
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={handleTabChange}
        fullWidth
      >
        {loading ? (
          <div className="space-y-6 p-6">
            {/* Header skeleton */}
            <Card>
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-4">
                    <SkeletonAvatar size="lg" shape="square" />
                    <div className="space-y-2">
                      <SkeletonText width="3/4" height="lg" />
                      <div className="flex items-center gap-2">
                        <Skeleton className="h-6 w-16 rounded-full" />
                        <Skeleton className="h-6 w-20 rounded-full" />
                      </div>
                      <SkeletonText width="1/3" height="sm" />
                    </div>
                  </div>
                  <div className="space-y-2 text-right">
                    <SkeletonText width="w-32" height="sm" />
                    <SkeletonText width="w-40" height="sm" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Tab content skeleton */}
            <SkeletonCard padding="lg" />
          </div>
        ) : !org ? (
          <div className="p-6">
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                Failed to load organization.
              </CardContent>
            </Card>
          </div>
        ) : (
          <div className="space-y-6 p-6">
            {/* Header summary card */}
            <Card>
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-4">
                    {org.logo_url ? (
                      <img
                        src={org.logo_url}
                        alt={org.name}
                        className="w-14 h-14 rounded-lg object-cover"
                      />
                    ) : (
                      <div className="w-14 h-14 rounded-lg bg-gray-200 flex items-center justify-center">
                        <Building2 className="h-7 w-7 text-gray-500" />
                      </div>
                    )}
                    <div>
                      <div className="flex items-center gap-2">
                        <h2 className="text-xl font-semibold">{org.name}</h2>
                        {org.is_test_org && (
                          <Badge variant="secondary">Test</Badge>
                        )}
                        {subscriptionChip}
                        {org.plan_name && (
                          <Badge variant="outline">{org.plan_name}</Badge>
                        )}
                      </div>
                      <div className="text-sm text-gray-600 mt-1 flex items-center gap-3">
                        <span className="text-xs">ID: {org.id}</span>
                        {org.website && (
                          <a
                            href={
                              org.website.startsWith("http")
                                ? org.website
                                : `https://${org.website}`
                            }
                            className="inline-flex items-center text-blue-600 hover:underline"
                            target="_blank"
                            rel="noreferrer"
                          >
                            {org.website.replace(/^https?:\/\//, "")}
                            <ExternalLink className="h-3.5 w-3.5 ml-1" />
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="text-xs text-gray-500 text-right">
                    <div>Created: {org.created_at || "-"}</div>
                    <div>
                      Current period end: {org.current_period_end || "-"}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Tab content placeholder */}
            {activeTab === "overview" && (
              <OrganizationOverviewTab
                organizationId={org.id}
                onboardingMetaFromHeader={null}
              />
            )}
            {activeTab === "billing" && (
              <OrganizationBillingTabEnhanced organizationId={org.id} />
            )}
            {activeTab === "members" && (
              <MembersTab organizationId={org.id} organizationName={org.name} />
            )}
            {activeTab !== "overview" &&
              activeTab !== "billing" &&
              activeTab !== "members" && (
                <Card>
                  <CardContent className="p-8 text-center text-muted-foreground">
                    {activeTab === "usage" && "Usage & Limits tab coming soon"}
                    {activeTab === "agents" && "Agents tab coming soon"}
                    {activeTab === "assistants" && "Assistants tab coming soon"}
                    {activeTab === "knowledge" &&
                      "Knowledge & Files tab coming soon"}
                    {activeTab === "integrations" &&
                      "Integrations tab coming soon"}
                    {activeTab === "settings" &&
                      "Settings & Flags tab coming soon"}
                    {activeTab === "audit" && "Audit Logs tab coming soon"}
                    {activeTab === "events" &&
                      "Webhooks/Events tab coming soon"}
                  </CardContent>
                </Card>
              )}
          </div>
        )}
      </SuperAdminLayout>
    </MainLayout>
  );
};

export default OrganizationDetailPage;
